import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';

interface AuditEntry {
  id: string;
  admin_user_id: string;
  admin_email: string;
  action: string;
  target_type: string;
  target_id: string;
  details: Record<string, unknown>;
  created_at: string;
}

export function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ items: AuditEntry[]; total: number }>('/admin/audit-log', {
        page: String(page),
        per_page: '25',
      });
      setEntries(res.data?.items ?? []);
      setTotalPages(Math.ceil((res.data?.total ?? 0) / 25));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading audit log...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No audit entries</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Timestamp</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Admin</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Action</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Target</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-gray-700">{entry.admin_email}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                      {entry.action}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    {entry.target_type}:{entry.target_id}
                  </td>
                  <td className="py-3 px-4 text-gray-500 max-w-xs truncate">
                    {JSON.stringify(entry.details)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 min-h-[44px]"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 min-h-[44px]"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
